import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CalendarDays, Clock, MapPin, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/api";
import { displayEventDate, formatDate } from "@/constants";
import ParticipationDialog from "@/components/ParticipationDialog";

const THEMES = [
  { match: ["halloween"], gradient: "from-[#3b1470] via-[#241040] to-[#7c2d12]", badge: "bg-orange-500 text-white", btn: "bg-orange-500 hover:bg-orange-400 text-white" },
  { match: ["noël", "noel"], gradient: "from-[#0c3b5e] via-[#10243d] to-[#14532d]", badge: "bg-sky-500 text-white", btn: "bg-red-500 hover:bg-red-400 text-white" },
  { match: ["octobre rose"], gradient: "from-[#831843] via-[#500f2b] to-[#9d174d]", badge: "bg-pink-500 text-white", btn: "bg-pink-500 hover:bg-pink-400 text-white" },
  { match: ["chandeleur"], gradient: "from-[#92400e] via-[#451a03] to-[#b45309]", badge: "bg-amber-500 text-slate-900", btn: "bg-amber-400 hover:bg-amber-300 text-slate-900" },
  { match: ["épiphanie", "epiphanie"], gradient: "from-[#854d0e] via-[#422006] to-[#a16207]", badge: "bg-yellow-500 text-slate-900", btn: "bg-yellow-400 hover:bg-yellow-300 text-slate-900" },
];
const DEFAULT_THEME = { gradient: "from-emerald-900 via-[#0d3b33] to-sky-900", badge: "bg-emerald-500 text-white", btn: "bg-amber-400 hover:bg-amber-300 text-slate-900" };

const themeFor = (title) => {
  const t = (title || "").toLowerCase();
  return THEMES.find((th) => th.match.some((m) => t.includes(m))) || DEFAULT_THEME;
};

const dayIso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const utcDay = (iso) => Date.UTC(+iso.slice(0, 4), +iso.slice(5, 7) - 1, +iso.slice(8, 10));

export default function NextEventBanner() {
  const [events, setEvents] = useState(null);
  const [now, setNow] = useState(() => new Date());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [lightbox, setLightbox] = useState(false);

  useEffect(() => {
    api.get("/events").then((r) => setEvents(r.data)).catch(() => setEvents([]));
    const timer = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  if (events === null) {
    return <div className="relative rounded-3xl bg-slate-900/90 h-80 animate-pulse" data-testid="next-event-loading" aria-hidden="true" />;
  }

  const today = dayIso(now);
  const next = events
    .filter((e) => e.date && e.date.slice(0, 10) >= today)
    .sort((a, b) => a.date.localeCompare(b.date))[0];

  if (!next) {
    return (
      <div className="theme-keep relative overflow-hidden rounded-3xl shadow-xl text-white bg-gradient-to-br from-emerald-900 via-[#0d3b33] to-sky-900 p-6 sm:p-8" data-testid="next-event-empty">
        <span className="inline-flex items-center gap-1.5 text-[11px] font-bold tracking-widest uppercase rounded-full px-3 py-1.5 bg-emerald-500 text-white">
          <CalendarDays className="h-3.5 w-3.5" /> Prochain événement
        </span>
        <h3 className="text-2xl font-extrabold tracking-tight mt-4">Le programme arrive bientôt</h3>
        <p className="text-sm text-white/75 mt-2">Aucun événement à venir pour le moment — revenez vite, la saison se prépare !</p>
        <Button variant="outline" className="mt-5 border-white/40 text-white hover:bg-white/10 hover:text-white" asChild>
          <Link to="/evenements">Voir le calendrier <ArrowRight className="h-4 w-4 ml-2" /></Link>
        </Button>
      </div>
    );
  }

  const theme = themeFor(next.title);
  const days = Math.round((utcDay(next.date.slice(0, 10)) - utcDay(today)) / 86400000);
  const hasTime = !next.date.includes("T00:00:00");
  const time = hasTime ? new Date(next.date).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }) : null;

  return (
    <div className={`theme-keep relative overflow-hidden rounded-3xl shadow-xl text-white bg-gradient-to-br ${theme.gradient}`} data-testid="next-event-banner">
      <div className="absolute -top-10 -right-10 w-48 h-48 bg-white/10 rounded-full blur-3xl" aria-hidden="true" />
      <div className="relative p-6 sm:p-8 space-y-4">
        <div className="flex items-start justify-between gap-4">
          <Link to={`/evenements#event-${next.id}`} title="Voir la fiche de l'événement" data-testid="next-event-kicker-link"
            className={`inline-flex items-center gap-1.5 text-[11px] font-bold tracking-widest uppercase rounded-full px-3 py-1.5 ${theme.badge} hover:brightness-110 transition`}>
            <CalendarDays className="h-3.5 w-3.5" /> Prochain événement
          </Link>
          <Link to={`/evenements#event-${next.id}`} title="Voir la fiche de l'événement" data-testid="next-event-countdown-link"
            className="text-center bg-white/10 backdrop-blur rounded-2xl px-4 py-2 border border-white/20 shrink-0 hover:bg-white/20 transition-colors block">
            <p className="text-2xl sm:text-3xl font-extrabold leading-none">J-{days}</p>
            <p className="text-[10px] uppercase tracking-wider text-white/80 mt-1">
              {days === 0 ? "c'est aujourd'hui !" : days === 1 ? "c'est demain !" : "avant l'événement"}
            </p>
          </Link>
        </div>

        <h3 className="text-2xl sm:text-3xl font-extrabold tracking-tight leading-tight" data-testid="next-event-title">{next.title}</h3>

        <div className="space-y-1.5 text-sm text-white/90">
          <p className="flex items-center gap-2" data-testid="next-event-date">
            <CalendarDays className="h-4 w-4 shrink-0" />
            {hasTime ? `${formatDate(next.date)} — à partir de ${time}` : formatDate(next.date)}
            {hasTime && <Clock className="h-4 w-4 hidden" aria-hidden="true" />}
          </p>
          {next.location && (
            <p className="flex items-center gap-2" data-testid="next-event-location"><MapPin className="h-4 w-4 shrink-0" />{next.location}</p>
          )}
          <p className="flex items-center gap-2"><Users className="h-4 w-4 shrink-0" />{next.members_only ? "Réservé aux adhérents" : "Ouvert aux familles"}</p>
        </div>

        {next.description && <p className="text-sm text-white/75 line-clamp-2 leading-relaxed">{next.description}</p>}

        {next.image_url && (
          <button type="button" onClick={() => setLightbox(true)} className="block cursor-zoom-in" data-testid="next-event-image-btn"
            aria-label="Agrandir l'affiche de l'événement" title="Cliquer pour agrandir">
            <img src={next.image_url} alt={`Affiche ${next.title}`}
              className="h-28 w-auto rounded-xl border border-white/20 shadow-lg hover:opacity-90 hover:scale-[1.02] transition" data-testid="next-event-image" />
          </button>
        )}

        <div className="flex flex-wrap gap-3 pt-1">
          {next.registration_open === false ? (
            <Button className="font-semibold shadow-md bg-slate-500 hover:bg-slate-600 text-white" onClick={() => setDialogOpen(true)} data-testid="next-event-participate-btn">
              Événement complet — inscriptions clôturées
            </Button>
          ) : (
            <Button className={`font-semibold shadow-md ${theme.btn}`} onClick={() => setDialogOpen(true)} data-testid="next-event-participate-btn">
              Je participe à l'événement
            </Button>
          )}
          <Button variant="outline" className="border-white/40 text-white hover:bg-white/10 hover:text-white bg-transparent" asChild data-testid="next-event-discover-btn">
            <Link to={`/evenements#event-${next.id}`}>Découvrir l'événement <ArrowRight className="h-4 w-4 ml-2" /></Link>
          </Button>
        </div>
      </div>
      {lightbox && next.image_url && (
        <div className="fixed inset-0 z-[70] bg-slate-950/85 backdrop-blur-sm flex items-center justify-center p-4 cursor-zoom-out animate-fade-up"
          onClick={() => setLightbox(false)} role="dialog" aria-label="Affiche de l'événement en grand" data-testid="next-event-lightbox">
          <img src={next.image_url} alt={`Affiche ${next.title}`} className="max-h-[88vh] max-w-full rounded-2xl shadow-2xl" />
        </div>
      )}
      <ParticipationDialog event={next} open={dialogOpen} onOpenChange={setDialogOpen} />
    </div>
  );
}
